import React from 'react';
import { Activity, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card } from '@/components/ui/Card';

interface HealthStats {
  totalLogs?: number;
  seizureFreeDays?: number;
  therapySessions?: number;
  medicationAdherence?: number;
  trends?: {
    logs?: 'up' | 'down' | 'stable';
    seizures?: 'up' | 'down' | 'stable';
  };
}

interface HealthOverviewWidgetProps {
  stats?: HealthStats | null;
  isLoading?: boolean;
  childName?: string;
}

function TrendIcon({ trend }: { trend?: 'up' | 'down' | 'stable' }) {
  if (trend === 'up') return <TrendingUp className="w-4 h-4 text-green-500" />;
  if (trend === 'down') return <TrendingDown className="w-4 h-4 text-red-500" />;
  return <Minus className="w-4 h-4 text-gray-400" />;
}

export function HealthOverviewWidget({ 
  stats, 
  isLoading, 
  childName 
}: HealthOverviewWidgetProps): React.ReactElement {
  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  const statCards = [
    {
      label: 'Total Entries',
      value: stats?.totalLogs ?? 0,
      icon: <Activity className="w-5 h-5 text-sage" />,
      trend: stats?.trends?.logs,
    },
    {
      label: 'Seizure-Free Days',
      value: stats?.seizureFreeDays ?? 0,
      icon: <Activity className="w-5 h-5 text-green-500" />,
      trend: stats?.trends?.seizures,
    },
    {
      label: 'Therapy Sessions',
      value: stats?.therapySessions ?? 0,
      icon: <Activity className="w-5 h-5 text-blue-500" />,
    },
    {
      label: 'Med Adherence',
      value: `${stats?.medicationAdherence ?? 0}%`,
      icon: <Activity className="w-5 h-5 text-purple-500" />,
    },
  ];

  return (
    <Card className="p-6">
      <h3 className="font-display font-semibold text-navy mb-4">
        {childName ? `${childName}'s Health Overview` : 'Health Overview'}
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className="bg-cream/50 rounded-lg p-4 flex flex-col"
          >
            <div className="flex items-center justify-between mb-2">
              {stat.icon}
              {stat.trend && <TrendIcon trend={stat.trend} />}
            </div>
            <span className="text-2xl font-bold text-navy">{stat.value}</span>
            <span className="text-sm text-warm-gray">{stat.label}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default HealthOverviewWidget;
